import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { StatutSante } from '@artosera/shared';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { LienConsole, Section } from './composants.js';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import styles from './Sante.module.css';

const POINT: Record<StatutSante, string> = {
  ok: commun.pointVert!,
  a_surveiller: commun.pointAmbre!,
  degrade: commun.pointRouge!,
};

/** Le feu : trois lampes, celle du statut allumée. */
function Feu({ statut }: { statut: StatutSante }): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <span className={styles.feu} role="img" aria-label={t(`vueDAigle.sante.feu.${statut}`)}>
      <i className={statut === 'degrade' ? styles.onRouge : undefined} />
      <i className={statut === 'a_surveiller' ? styles.onAmbre : undefined} />
      <i className={statut === 'ok' ? styles.onVert : undefined} />
    </span>
  );
}

/**
 * La santé de la plateforme : un statut d'ensemble et ses points. La vue
 * associé n'a que `base`, `stockage`, `travaux` et `sauvegardes` (statut et
 * date), jamais le détail de la file de travaux : ce qui est servi s'affiche.
 */
export function Sante({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);
  const { sante } = vue;

  return (
    <Section
      id="sante"
      numero={numero}
      titre={t('vueDAigle.sante.titre')}
      note={t('vueDAigle.sante.note', { date: f.dateHeure(vue.genereeLe) })}
      actions={
        peut('console:read') ? (
          <LienConsole vers="/sante">{t('vueDAigle.sante.detail')}</LienConsole>
        ) : undefined
      }
    >
      <div className={styles.global}>
        <Feu statut={sante.statut} />
        <div>
          <h3>{t(`vueDAigle.sante.global.${sante.statut}.titre`)}</h3>
          <p>{t(`vueDAigle.sante.global.${sante.statut}.texte`)}</p>
        </div>
      </div>
      {sante.points.length === 0 ? (
        <p className={commun.noteDiscrete}>{t('vueDAigle.sante.aucunPoint')}</p>
      ) : (
        <ul className={styles.grille}>
          {sante.points.map((point) => (
            <li key={point.cle} className={styles.item}>
              <span className={styles.etat}>
                <span className={`${commun.point} ${POINT[point.statut]}`} aria-hidden="true" />
                {t(`vueDAigle.sante.statut.${point.statut}`)}
              </span>
              <span className={styles.nom}>
                {t(`vueDAigle.sante.point.${point.cle}.titre`, { defaultValue: point.cle })}
              </span>
              {point.depuis ? (
                <p>
                  {t(`vueDAigle.sante.point.${point.cle}.depuis`, {
                    date: f.dateHeure(point.depuis),
                    defaultValue: t('vueDAigle.sante.pointDefaut', {
                      date: f.dateHeure(point.depuis),
                    }),
                  })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
